import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Briefcase,
  Phone,
  Globe,
  MapPin,
  ChevronLeft,
  Loader2,
  Save,
  BarChart3,
  FileText,
  ExternalLink,
} from "lucide-react";
import { useWorkspace } from "@/hooks/use-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const MARKET_OPTIONS = [
  "Primary — Gateway Cities",
  "Secondary — Growth Markets",
  "Tertiary — Emerging Markets",
  "Sun Belt",
  "Midwest",
  "Northeast Corridor",
  "West Coast",
  "Southeast",
];


const Profile = () => {
  const navigate = useNavigate();
  const { workspaces, loading: wsLoading } = useWorkspace();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    full_name: "",
    role: "",
    phone: "",
    bio: "",
    linkedin_url: "",
    markets_of_interest: [] as string[],
  });
  const [stats, setStats] = useState({ deals_analyzed: 0, total_units_analyzed: 0 });

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/auth"); return; }

      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (data) {
        setProfile({
          full_name: data.full_name || "",
          role: (data as any).role || "",
          phone: (data as any).phone || "",
          bio: (data as any).bio || "",
          linkedin_url: (data as any).linkedin_url || "",
          markets_of_interest: (data as any).markets_of_interest || [],
        });
        setStats({
          deals_analyzed: (data as any).deals_analyzed || 0,
          total_units_analyzed: (data as any).total_units_analyzed || 0,
        });
      }
      setLoading(false);
    };
    load();
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("profiles")
      .update({
        full_name: profile.full_name,
        role: profile.role,
        phone: profile.phone,
        bio: profile.bio,
        linkedin_url: profile.linkedin_url,
        markets_of_interest: profile.markets_of_interest,
        updated_at: new Date().toISOString(),
      } as any)
      .eq("id", user.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Profile saved" });
    }
    setSaving(false);
  };

  const toggleMarket = (market: string) => {
    setProfile((p) => ({
      ...p,
      markets_of_interest: p.markets_of_interest.includes(market)
        ? p.markets_of_interest.filter((m) => m !== market)
        : [...p.markets_of_interest, market],
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-border/50 sticky top-0 bg-background/80 backdrop-blur-md z-50">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="w-4 h-4" />
          <span className="font-mono text-xs">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-accent flex items-center justify-center">
            <span className="font-display text-accent-foreground text-xs font-bold">B</span>
          </div>
          <span className="font-display text-sm font-semibold tracking-tight">
            Brickshare<span className="text-accent">.ai</span>
          </span>
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90 font-mono text-xs">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          <span className="ml-1">Save</span>
        </Button>
      </nav>

      <div className="px-6 py-10 max-w-2xl mx-auto w-full space-y-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Your Profile</h1>
          <p className="text-sm text-muted-foreground">Complete your profile to personalize deal analysis and reporting.</p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg border border-border bg-card">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-accent" />
              <span className="font-mono text-xs text-muted-foreground uppercase">Deals Analyzed</span>
            </div>
            <span className="font-display text-2xl font-bold">{stats.deals_analyzed}</span>
          </div>
          <div className="p-4 rounded-lg border border-border bg-card">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-4 h-4 text-accent" />
              <span className="font-mono text-xs text-muted-foreground uppercase">Total Units</span>
            </div>
            <span className="font-display text-2xl font-bold">{stats.total_units_analyzed.toLocaleString()}</span>
          </div>
        </div>

        {/* Personal */}
        <section className="space-y-4">
          <h2 className="font-display text-lg font-semibold">Personal Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={profile.full_name} onChange={(e) => setProfile((p) => ({ ...p, full_name: e.target.value }))} placeholder="Your name" className="pl-10 font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} placeholder="(555) 123-4567" maxLength={10} className="pl-10 font-mono" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Bio</Label>
            <Textarea value={profile.bio} onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))} placeholder="Brief summary of your investment experience and focus areas..." className="font-mono min-h-[80px]" />
          </div>
        </section>

        {/* Business */}
        <section className="space-y-4">
          <h2 className="font-display text-lg font-semibold">Business Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Role / Title</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={profile.role} onChange={(e) => setProfile((p) => ({ ...p, role: e.target.value }))} placeholder="e.g. GP, LP, Analyst, Broker" className="pl-10 font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Personal LinkedIn</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={profile.linkedin_url} onChange={(e) => setProfile((p) => ({ ...p, linkedin_url: e.target.value }))} placeholder="https://linkedin.com/in/yourprofile" className="pl-10 font-mono" />
              </div>
            </div>
          </div>
        </section>

        {/* Markets */}
        <section className="space-y-4">
          <h2 className="font-display text-lg font-semibold">Markets of Interest</h2>
          <div className="space-y-2">
            {MARKET_OPTIONS.map((m) => (
              <label key={m} className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:border-accent/30 transition-colors cursor-pointer">
                <Checkbox checked={profile.markets_of_interest.includes(m)} onCheckedChange={() => toggleMarket(m)} />
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-sm">{m}</span>
              </label>
            ))}
          </div>
        </section>

        {/* My Workspaces */}
        <section className="space-y-4">
          <h2 className="font-display text-lg font-semibold">My Workspaces</h2>
          {wsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : workspaces.length === 0 ? (
            <p className="text-sm text-muted-foreground">You are not a member of any workspace yet.</p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-mono text-xs uppercase">Workspace</TableHead>
                    <TableHead className="font-mono text-xs uppercase">Role</TableHead>
                    <TableHead className="font-mono text-xs uppercase text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workspaces.map((ws) => (
                    <TableRow key={ws.id}>
                      <TableCell className="font-medium">{ws.name}</TableCell>
                      <TableCell>
                        <Badge variant={ws.role === "owner" ? "default" : "secondary"} className="font-mono text-2xs uppercase">
                          {ws.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 font-mono text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => navigate(`/workspace/${ws.id}`)}
                        >
                          View Details
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        <Button onClick={handleSave} disabled={saving} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-display h-11">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Save className="w-4 h-4" /> Save Profile</>}
        </Button>
      </div>
    </div>
  );
};

export default Profile;
